import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {ResultBlobGraphQL} from '../../../graphql-fragments/results/__generated/ResultBlobGraphQL';
import {runAutomationOutputFileStatusModal} from './AutomationFileStatusModal';
import SucceededAutomationFileButton from './SucceededAutomationFileButton';

interface SucceededRunAutomationOutputFileButtonProps {
  automationOutputFile: AutomationOutputFileGraphQL & {blob: ResultBlobGraphQL};
  isStale: boolean;
  runId: string | null;
  configName: string | null;
  locked: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  previousFileNames: Set<string>;
}

/**
 * This component represents an AutomationOutputFile with a SUCCEEDED status in a NoteLinkedAssayData.
 */
const SucceededRunAutomationOutputFileButton: React.FunctionComponent<
  SucceededRunAutomationOutputFileButtonProps
> = (props: SucceededRunAutomationOutputFileButtonProps): JSX.Element => {
  const hasErrors = props.automationOutputFile.rowLevelErrorsBlob;
  const editorPropertiesContext = EditorPropertiesContext.useContext();
  const openStatusModal = (): void => {
    runAutomationOutputFileStatusModal({
      automationFile: props.automationOutputFile,
      runId: props.runId,
      configName: props.configName,
      locked: props.locked,
      updateAutomationOutputFiles: props.updateAutomationOutputFiles,
      previousFileNames: props.previousFileNames,
      editorPropertiesContext,
    });
  };
  return (
    <SucceededAutomationFileButton
      buttonStyle={hasErrors ? ButtonStyle.WARNING : ButtonStyle.TERTIARY_GRAY}
      buttonText={hasErrors ? 'Completed with errors' : 'Complete'}
      buttonIcon={
        hasErrors ? (
          'warning'
        ) : (
          <BenchlingIcon
            className={css(styles.buttonIcon)}
            icon="check"
            dataTestAttrs={{
              component: 'SucceededRunAutomationOutputFileButton',
              element: 'checkIcon',
            }}
          />
        )
      }
      isStale={props.isStale}
      locked={props.locked}
      handleRefreshVersionedData={props.handleRefreshVersionedData}
      onClick={openStatusModal}
      dataTestAttrs={{
        component: 'SucceededRunAutomationOutputFileButton',
        element: 'button',
      }}
    />
  );
};

export default SucceededRunAutomationOutputFileButton;

const styles = StyleSheet.create({
  buttonIcon: {
    color: Colors.GREEN,
  },
});
